/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Alert from 'react-s-alert';
import { Link } from 'react-router-dom';
import CKEditor from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Joyride, { STATUS } from 'react-joyride';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// service imports
import ProviderService from '../../services/ProviderService';
import GeneralService from '../../services/GeneralService';
import AuthService from '../../services/AuthService';

// local imports
import OrganisationDashboardImg from '../../assets/organisationDashboard.svg';
import ErrorBoundary from '../general/ErrorBoundary';
import Message from '../general/Message';
import ProviderNavbar from './ProviderNavbar';
import FullPageLoader from '../general/FullPageLoader';
import UpcomingSession from '../user/UpcomingSession';
import CompletedSession from '../user/CompletedSession';
import CancelledSession from '../user/CancelledSession';
import RescheduleRequestedSession from '../user/RescheduleRequestedSession';
import ReservedSession from '../user/ReservedSession';
import UserNotApproved from './UserNotApproved';

const Sessions = props => {

    // variables
    let timeout = 0;

    // contexts
    const { theme, colors, fontFamily, fontSize, fontWeight } = useContext(ThemeContext);
    const { user, setUser, selectedTimeZone, userApprovalStatus } = useContext(AuthContext);

    // states
    const [isLoading, setIsLoading] = useState(false);
    const [listOfSessions, setListOfSessions] = useState({
        completedSessions: [],
        upcomingSessions: [],
        cancelledSessions: [],
        rescheduleRequestedSessions: [],
        reservedSessions: []
    });
    const [notifications, setNotifications] = useState({
        newUpcomingSession: [],
        newRescheduledSession: [],
        newRefundProcessed: [],
        newChatMessage: []
    });
    const [editor, setEditor] = useState();
    const [modal, setModal] = useState({
        visible: false,
        sessionId: null,
        sessionStatus: "",
        sessionIndex: -1,
        notes: null
    });
    const [loading, setLoading] = useState(true);
    const [isRefreshLoading, setIsRefreshLoading] = useState(false);
    const [profileComplete, setProfileComplete] = useState({
        status: true,
        message: {
            msgBody: [`Looks like your profile is incomplete! Please complete that by going to `, <b key="profileLink"><Link to='/counsellor/profile'>Profile</Link></b>, ` page to enjoy our service. Unless your profile is completed you won't be visible to any clients.`],
            msgError: true
        }
    });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h5>Welcome to the guided tour of Umang Wellness. We are excited to have you here as a Umang Wellness counsellor.<br />Press Next to continue.</h5>,
                placement: 'center',
                target: '#sessionsDiv',
            },
            {
                content: <div className="tooltipDiv"><h5>This is your dashboard!</h5><br />From here you can manage your sessions with clients.</div>,
                placement: 'center',
                target: '#sessionsDiv',
            },
            {
                content: <div className="tooltipDiv">Start chat with your clients by pressing this button.</div>,
                placement: 'left',
                target: '#chatBtn',
            },
            {
                content: <div className="tooltipDiv">Use this button to refresh your dashboard data</div>,
                placement: 'left',
                target: '#refreshBtn',
            },
            {
                content: <div className="tooltipDiv">Find your upcoming sessions here</div>,
                placement: 'bottom',
                target: '#upcomingSessions',
            },
            {
                content: <div className="tooltipDiv">Add Notes about client sessions here.</div>,
                placement: 'bottom',
                target: '#notesBtn0'
            },
            {
                content: <div className="tooltipDiv">Send a request to reschedule a session to the client in case your plans change by pressing this button.</div>,
                placement: 'bottom',
                target: '#requestRescheduleBtn0'
            },
            {
                content: <div className="tooltipDiv">Mark session as complete by pressing this button.</div>,
                placement: 'bottom',
                target: '#markCompleteBtn0'
            },
            {
                content: <div className="tooltipDiv">Cancel a session by pressing this button.</div>,
                placement: 'bottom',
                target: '#cancelBtn0'
            },
            {
                content: <div className="tooltipDiv">Start session by pressing this button.</div>,
                placement: 'bottom',
                target: '#meetBtn0'
            },
            {
                content: <div className="tooltipDiv">Reschedule requests to your client will appear here.</div>,
                placement: 'top',
                target: '#rescheduledSessions',
            },
            {
                content: <div className="tooltipDiv">Completed sessions will appear here.</div>,
                placement: 'top',
                target: '#completedSessions',
            },
            {
                content: <div className="tooltipDiv">Cancelled sessions will appear here.</div>,
                placement: 'top',
                target: '#cancelledSessions',
            },
            {
                content: <div className="tooltipDiv">Navigate to other options by pressing your profile picture.</div>,
                placement: 'left',
                target: '#profileDropdown'
            },
            {
                content: <div className="tooltipDiv">Start this guided tour again by pressing your profile picture and selecting <b>Guided Tour</b>.</div>,
                placement: 'left',
                target: '#profileDropdown'
            },
            {
                content: <div className="tooltipDiv"><h5>Hope you enjoyed the quick tour. Please give us your <a style={{ fontWeight: "600", color: "#007bff" }} target="_blank" href="/feedback">feedback</a>.</h5></div>,
                placement: 'center',
                target: '#sessionsDiv'
            }
        ]
    });
    let helpers = null;

    // useEffect
    useEffect(() => {
        setLoading(true);
        getSessions();
        if (!user.guideTakenFor.includes("Sessions")) {
            setTutorial({ ...tutorial, status: true });
        }
    }, []);

    const getNotifications = () => {
        AuthService.getNotifications().then(data => {
            try {
                let temp = { newUpcomingSession: [], newRescheduledSession: [], newRefundProcessed: [], newChatMessage: [] };
                Promise.all(data.notifications.ProviderSession.map(item => {
                    if (!item.readStatus) {
                        if (item.title === "newUpcomingSession") {
                            temp.newUpcomingSession.push(item.description);
                        } else if (item.title === "newRescheduledSession") {
                            temp.newRescheduledSession.push(item.description);
                        } else if (item.title === "newRefundProcessed") {
                            temp.newRefundProcessed.push(item.description);
                        }
                        AuthService.markNotificationAsRead("ProviderSession", item._id);
                    }
                })).then(() => {
                    Promise.all(data.notifications.ProviderChat.map(item => {
                        if (!item.readStatus && item.title === "newChatMessage") {
                            temp.newChatMessage.push(item.description);
                        }
                    })).then(() => {
                        setNotifications(temp);
                    })
                });
            } catch (err) {
                Alert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                GeneralService.logError('at Provider Sessions getNotifications', 'Error in try catch block', err.stack);
            }
        })
    }

    // utility functions
    const getSessions = () => {
        try {
            ProviderService.getSessions().then(sessions => {
                setProfileComplete({ ...profileComplete, status: sessions.profileComplete });
                setListOfSessions(sessions);
                getNotifications();
                setLoading(false);
                setIsRefreshLoading(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsRefreshLoading(false);
            GeneralService.logError('at Provider Sessions getSessions', 'Error in try catch block', err.stack);
        }
    }

    // handle functions
    const handleRefresh = () => {
        setIsRefreshLoading(true);
        getSessions();
    }

    const handleNotesChange = (data) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
            setModal({ ...modal, notes: data });
        }, 500);
    }

    const handleNotesModal = (data) => {
        setModal(data);
    }

    const handleNotesSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading(true);
            ProviderService.setNotes(modal)
                .then(data => {
                    var temp = { ...listOfSessions };
                    if (modal.sessionStatus === "Completed") {
                        temp.completedSessions[modal.sessionIndex].notes = modal.notes;
                    } else if (modal.sessionStatus === "Upcoming") {
                        temp.upcomingSessions[modal.sessionIndex].notes = modal.notes;
                    } else if (modal.sessionStatus === "Cancelled") {
                        temp.cancelledSessions[modal.sessionIndex].notes = modal.notes;
                    } else if (modal.sessionStatus === "Recheduled") {
                        temp.rescheduleRequestedSessions[modal.sessionIndex].notes = modal.notes;
                    }
                    setListOfSessions(temp);
                    setModal({ visible: false, sessionId: null, notes: null })
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Provider Sessions handleNotesSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.guideTakenFor.includes("Sessions")) {
                AuthService.markVisited("Sessions");
                setUser({ ...user, guideTakenFor: [...user.guideTakenFor, "Sessions"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    return (
        loading ?
            <FullPageLoader /> :
            <div id="sessionsDiv">
                <ErrorBoundary errorAt="At Provider Sessions ProviderNavbar">
                    <ProviderNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
                </ErrorBoundary>
                {
                    userApprovalStatus ?
                        <div>
                            <ErrorBoundary errorAt="At Provider Sessions Joyride">
                                <Joyride
                                    callback={handleJoyrideCallback}
                                    continuous={true}
                                    getHelpers={getHelpers}
                                    run={tutorial.status}
                                    scrollToFirstStep={true}
                                    showProgress={true}
                                    showSkipButton={true}
                                    steps={tutorial.steps}
                                    locale={{ last: 'Finish' }}
                                    styles={{
                                        options: {
                                            primaryColor: "rgb(56, 78, 115)",
                                            zIndex: 10000,
                                        },
                                    }}
                                />
                            </ErrorBoundary>
                            <div className="p-3 container-fluid">
                                <div className="d-flex">
                                    <h1 id="session-heading" className="my-auto" style={theme.headingBold}>Sessions</h1>
                                    <Button id="chatBtn" className="btn ml-auto mr-2" style={theme.button} onClick={() => props.history.push('chat')}>Chat{notifications.newChatMessage.length > 0 ? <Badge className="ml-1" style={{ backgroundColor: colors.warning }}>{notifications.newChatMessage.length}</Badge> : null}</Button>
                                    <Button id="refreshBtn" className="btn" style={theme.button} onClick={handleRefresh} disabled={isRefreshLoading}>{isRefreshLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Refresh</Button>
                                </div>
                                <hr style={{ borderWidth: "2px" }} />

                                {profileComplete.status ? null : <Message message={profileComplete.message} />}

                                {
                                    listOfSessions.reservedSessions.length > 0 ?
                                        <ErrorBoundary errorAt="At Provider Sessions ReservedSession">
                                            <ReservedSession handleModal={(data) => handleNotesModal(data)} sessions={listOfSessions.reservedSessions} /*newUpcomingSession={notifications.newUpcomingSession}*/ />
                                        </ErrorBoundary>
                                        : null
                                }

                                <ErrorBoundary errorAt="At Provider Sessions UpcomingSession">
                                    <UpcomingSession handleModal={(data) => handleNotesModal(data)} sessions={listOfSessions.upcomingSessions} newUpcomingSession={notifications.newUpcomingSession} newRescheduledSession={notifications.newRescheduledSession} />
                                </ErrorBoundary>

                                <ErrorBoundary errorAt="At Provider Sessions RescheduleRequestedSession">
                                    <RescheduleRequestedSession selectedTimeZone={selectedTimeZone} handleModal={(data) => handleNotesModal(data)} sessions={listOfSessions.rescheduleRequestedSessions} />
                                </ErrorBoundary>

                                <ErrorBoundary errorAt="At Provider Sessions CompletedSession">
                                    <CompletedSession selectedTimeZone={selectedTimeZone} handleModal={(data) => handleNotesModal(data)} sessions={listOfSessions.completedSessions} />
                                </ErrorBoundary>

                                <ErrorBoundary errorAt="At Provider Sessions CancelledSession">
                                    <CancelledSession selectedTimeZone={selectedTimeZone} handleModal={(data) => handleNotesModal(data)} sessions={listOfSessions.cancelledSessions} newRefundProcessed={notifications.newRefundProcessed} />
                                </ErrorBoundary>

                                <ErrorBoundary errorAt="At Provider Sessions Modal">
                                    <Modal size="lg" isOpen={modal.visible} backdrop={true}>
                                        <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setModal({ visible: false, sessionId: null, notes: null })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Notes</span></ModalHeader>
                                        <ModalBody>
                                            {
                                                modal.visible ?
                                                    <CKEditor
                                                        editor={ClassicEditor}
                                                        data={modal.notes}
                                                        config={{
                                                            placeholder: "You can take your notes with different styles",
                                                            toolbar: ['heading', '|', 'bold', 'italic', 'bulletedList', 'numberedList', '|', 'indent', 'outdent', '|', 'insertTable', 'undo', 'redo']
                                                        }}
                                                        onInit={editor => {
                                                            setEditor(editor);
                                                            editor.editing.view.change(writer => {
                                                                writer.setStyle("height", "22rem", editor.editing.view.document.getRoot());
                                                            });
                                                        }}
                                                        onChange={(event, editor) => {
                                                            const data = editor.getData();
                                                            handleNotesChange(data);
                                                        }}
                                                    />
                                                    : null
                                            }
                                        </ModalBody>
                                        <ModalFooter style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                            <Button type="button" style={theme.formSubmit} onClick={handleNotesSubmit} disabled={isLoading}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                        </ModalFooter>
                                    </Modal>
                                </ErrorBoundary>

                            </div>
                        </div>
                        :
                        <UserNotApproved />
                }
            </div>
    )
}

export default Sessions;